import React, { useEffect, useState } from "react";
import { supabase } from "../supabase/client";
import {
  FiUser,
  FiCheckCircle,
  FiXCircle,
  FiFilter,
  FiSearch,
  FiTrash2,
  FiDownload,
  FiEye,
  FiEyeOff,
  FiLock
} from "react-icons/fi";
import "./Users.css";

const Users = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [tierFilter, setTierFilter] = useState("all");
  const [activeOnly, setActiveOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);

  const [currentAdmin, setCurrentAdmin] = useState(null);

  // Get current admin user on component mount
  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // Get user details from users table
        const { data: userData, error } = await supabase
          .from("users")
          .select("email, role")
          .eq("id", user.id)
          .single();

        if (!error && userData) {
          setCurrentAdmin(userData);
        }
      }
    };

    getCurrentUser();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);

    try {
      // Fetch users and memberships separately to avoid relationship ambiguity
      const { data: usersData, error: usersError } = await supabase
        .from("users")
        .select("id, username, email, created_at, role, Active")
        .order("created_at", { ascending: false });

      if (usersError) throw usersError;

      // Fetch memberships separately
      const { data: membershipsData, error: membershipsError } = await supabase
        .from("memberships")
        .select("user_id, type, active_status, last_wash_date, expiry_date");

      if (membershipsError) throw membershipsError;

      // Fetch check-ins count separately
      const { data: checkinsData, error: checkinsError } = await supabase
        .from("check_ins")
        .select("user_id");

      if (checkinsError) throw checkinsError;

      // Count check-ins per user
      const checkinsCount = checkinsData?.reduce((acc, checkin) => {
        acc[checkin.user_id] = (acc[checkin.user_id] || 0) + 1;
        return acc;
      }, {}) || {};

      // Combine the data
      const combinedData = usersData.map(user => {
        const userMembership = membershipsData?.find(m => m.user_id === user.id);
        const checkinsCountValue = checkinsCount[user.id] || 0;

        return {
          ...user,
          membership: userMembership || null,
          checkinsCount: checkinsCountValue
        };
      });

      setUsers(combinedData);
      setFilteredUsers(combinedData);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  // Apply filters and search
  useEffect(() => {
    let filtered = users;

    // Apply active status filter
    if (activeOnly) {
      filtered = filtered.filter((u) => u.Active === true);
    }

    // Apply membership type filter
    if (tierFilter !== "all") {
      filtered = filtered.filter((u) => u.membership?.type === tierFilter);
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter((u) =>
        u.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.role?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredUsers(filtered);
  }, [users, tierFilter, activeOnly, searchTerm]);

  useEffect(() => {
    fetchUsers();
  }, []);

  // Check if current admin has permission to change roles
  const canChangeRoles = () => {
    return currentAdmin?.email === "cwash.hub@gmail.com";
  };

  const updateUserStatus = async (userId, active) => {
    const { error } = await supabase
      .from("users")
      .update({ Active: active })
      .eq("id", userId);

    if (!error) {
      fetchUsers();
    } else {
      console.error("Error updating user status:", error);
    }
  };

  const updateUserRole = async (userId, newRole) => {
    // Check permission before allowing role change
    if (!canChangeRoles()) {
      alert("Only the main administrator (cwash.hub@gmail.com) can change user roles.");
      return;
    }

    const { error } = await supabase
      .from("users")
      .update({ role: newRole })
      .eq("id", userId);

    if (!error) {
      fetchUsers();
    } else {
      console.error("Error updating user role:", error);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user? This action cannot be undone.")) {
      return;
    }

    const { error } = await supabase
      .from("users")
      .delete()
      .eq("id", userId);

    if (!error) {
      fetchUsers();
    } else {
      console.error("Error deleting user:", error);
      alert("Error deleting user. They may have related records in the system.");
    }
  };

  const exportToCSV = () => {
    const headers = ["Username", "Email", "Role", "Membership", "Status", "Last Wash", "Expiry Date", "Check-ins", "Created Date"];
    const csvData = filteredUsers.map(user => [
      user.username || "—",
      user.email,
      user.role,
      user.membership?.type || "—",
      user.membership?.active_status || "—",
      formatDate(user.membership?.last_wash_date),
      formatDate(user.membership?.expiry_date),
      user.checkinsCount,
      formatDate(user.created_at)
    ]);

    const csvContent = [
      headers.join(","),
      ...csvData.map(row => row.map(field => `"${field}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `users-export-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const getMembershipBadge = (type) => {
    const types = {
      Free: { color: "#6B7280", label: "Free" },
      Essential: { color: "#3B82F6", label: "Essential" },
      Enhanced: { color: "#F59E0B", label: "Enhanced" },
      Ultimate: { color: "#10B981", label: "Ultimate" },
    };
    return types[type] || { color: "#6B7280", label: "—" };
  };

  const getStatusBadge = (activeStatus) => {
    const statuses = {
      Active: { color: "success", label: "Active", icon: <FiCheckCircle /> },
      Paused: { color: "warning", label: "Paused", icon: <FiXCircle /> },
      Incomplete: { color: "warning", label: "Incomplete", icon: <FiXCircle /> },
      Cancelled: { color: "error", label: "Cancelled", icon: <FiXCircle /> },
    };
    return statuses[activeStatus] || { color: "inactive", label: "No Membership", icon: <FiXCircle /> };
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString();
  };

  const getDaysUntilExpiry = (expiryDate) => {
    if (!expiryDate) return null;
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="users-container">
      <div className="page-header">
        <h2 className="page-title">
          <FiUser /> User Management
          <span className="user-count">({filteredUsers.length} users)</span>
        </h2>

        <div className="filter-controls">
          <div className="search-group">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filter-group">
            <FiFilter className="filter-icon" />
            <select
              value={tierFilter}
              onChange={(e) => setTierFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Memberships</option>
              <option value="Free">Free</option>
              <option value="Essential">Essential</option>
              <option value="Enhanced">Enhanced</option>
              <option value="Ultimate">Ultimate</option>
            </select>
          </div>

          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => setActiveOnly(e.target.checked)}
            />
            <span className="slider"></span>
            <span>Active Only</span>
          </label>

          <button onClick={exportToCSV} className="export-btn">
            <FiDownload /> Export CSV
          </button>
        </div>
      </div>

      {/* Admin Permission Notice */}
      {currentAdmin && !canChangeRoles() && (
        <div className="permission-notice">
          <FiLock className="lock-icon" />
          <span>Role management restricted to main administrator only</span>
        </div>
      )}

      {loading ? (
        <div className="loading-spinner">Loading users...</div>
      ) : (
        <div className="table-container">
          <div className="stats-cards">
            <div className="stat-card">
              <div className="stat-value">{users.length}</div>
              <div className="stat-label">Total Users</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{users.filter(u => u.Active).length}</div>
              <div className="stat-label">Active Users</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{users.filter(u => u.membership?.active_status === 'Active').length}</div>
              <div className="stat-label">Active Memberships</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">
                {users.reduce((sum, user) => sum + user.checkinsCount, 0)}
              </div>
              <div className="stat-label">Total Check-ins</div>
            </div>
          </div>

          <table className="data-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role {!canChangeRoles() && <FiLock className="lock-icon-small" />}</th>
                <th>Membership</th>
                <th>Status</th>
                <th>Last Activity</th>
                <th>Check-ins</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length > 0 ? (
                filteredUsers.map((u) => {
                  const membershipBadge = getMembershipBadge(u.membership?.type);
                  const statusBadge = getStatusBadge(u.membership?.active_status);
                  const daysUntilExpiry = getDaysUntilExpiry(u.membership?.expiry_date);

                  return (
                    <tr key={u.id} className={!u.Active ? "user-inactive" : ""}>
                      <td>
                        <div className="user-info">
                          <div className="user-avatar">
                            {u.username?.charAt(0)?.toUpperCase() || u.email?.charAt(0)?.toUpperCase()}
                          </div>
                          <div>
                            <div className="user-name">{u.username || "—"}</div>
                            <div className="user-email">{u.email}</div>
                            <div className="user-joined">Joined {formatDate(u.created_at)}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        {canChangeRoles() ? (
                          <select
                            value={u.role}
                            onChange={(e) => updateUserRole(u.id, e.target.value)}
                            className="role-select"
                          >
                            <option value="user">User</option>
                            <option value="car_wash_staff">Staff</option>
                            <option value="admin">Admin</option>
                          </select>
                        ) : (
                          <div className="role-display">
                            <span className={`role-badge ${u.role}`}>
                              {u.role}
                            </span>
                            {u.role === 'admin' && <FiLock className="admin-lock-icon" />}
                          </div>
                        )}
                      </td>
                      <td>
                        <span
                          className="membership-badge"
                          style={{ backgroundColor: membershipBadge.color }}
                        >
                          {membershipBadge.label}
                        </span>
                        {daysUntilExpiry !== null && daysUntilExpiry <= 7 && (
                          <div className="expiry-warning">
                            {daysUntilExpiry <= 0 ? 'Expired' : `${daysUntilExpiry}d left`}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="status-container">
                          <span className={`status ${statusBadge.color}`}>
                            {statusBadge.icon}
                            {statusBadge.label}
                          </span>
                          <button
                            onClick={() => updateUserStatus(u.id, !u.Active)}
                            className={`status-toggle ${u.Active ? 'active' : 'inactive'}`}
                            title={u.Active ? 'Deactivate user' : 'Activate user'}
                          >
                            {u.Active ? <FiEye /> : <FiEyeOff />}
                          </button>
                        </div>
                      </td>
                      <td>
                        <div className="activity-info">
                          <div>Last wash: {formatDate(u.membership?.last_wash_date)}</div>
                          <div>Expires: {formatDate(u.membership?.expiry_date)}</div>
                        </div>
                      </td>
                      <td className="checkins-count">
                        <span className="checkins-badge">{u.checkinsCount}</span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            onClick={() => setSelectedUser(u)}
                            className="view-btn"
                            title="View user details"
                          >
                            <FiEye />
                          </button>
                          <button
                            onClick={() => deleteUser(u.id)}
                            className="delete-btn"
                            title="Delete user"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="7" className="no-results">
                    No users found matching your filters
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* User Detail Modal */}
      {selectedUser && (
        <div className="modal-overlay" onClick={() => setSelectedUser(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>User Details</h3>
            <div className="user-details">
              <div className="detail-row">
                <span className="detail-label">Username:</span>
                <span>{selectedUser.username || "—"}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Email:</span>
                <span>{selectedUser.email}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Role:</span>
                <span>{selectedUser.role}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Membership:</span>
                <span>{selectedUser.membership?.type || "—"}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Status:</span>
                <span>{selectedUser.membership?.active_status || "—"}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Check-ins:</span>
                <span>{selectedUser.checkinsCount}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Joined:</span>
                <span>{formatDate(selectedUser.created_at)}</span>
              </div>
            </div>
            <button
              className="close-btn"
              onClick={() => setSelectedUser(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;